import { useCallback, useRef } from 'react';

import {
  type AssistantDiscoveryTarget,
  getDiscoveryTarget,
} from '@revelio/core/global-ai-assistant/discovery-targets';
import type { ChartValueForm } from '@revelio/core/global-ai-assistant/share-display';
import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import {
  getValidatedPositionedItemsFromLlmContent,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { AssistantPostingsMode } from '../CustomDash/components/DataProvider/postings/postings-metric-mode';
import { summarizeChartPreviewTimeline } from './chart-preview-timeline-summary';
import { executeClientTool } from './client-tools/registry';
import {
  isClientToolFailure,
  isTurnCancelledResult,
  type StandardPageAnswerChart,
} from './client-tools/types';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantEntityType,
  postGlobalAssistantSelectReport,
  toCustomDashAssistantEntityType,
  toSingleChartAssistantCapabilityEntityType,
} from './global-ai-assistant-api';
import {
  useGlobalAiChartPreviewRuntimeStore,
  waitForChartSettle,
} from './global-ai-assistant-chart-preview-runtime';
import {
  type CommandExpiredOutcome,
  channelEventsSince,
  type GlobalAiChannelPayloads,
  useGlobalAiCommandStore,
  waitForChannelEvent,
  waitForCommandComplete,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import { toConversationPayload } from './global-ai-assistant-context-policy';
import { buildBoundedConversationContext } from './global-ai-assistant-conversation';
import { consumeContinuation } from './global-ai-assistant-engine-store';
import {
  getCurrentPageOptionId,
  getStandardPageMatchForOptionId,
} from './global-ai-assistant-standard-page-match';
import { USER_STOP_ABORT_REASON } from './global-ai-assistant-transcript';
import type {
  ClientToolHandles,
  NavigateToOptionResult,
} from './global-ai-client-tool-deps';
import { createClientToolDeps } from './global-ai-client-tool-deps';
import {
  generateDashboardHeadless,
  type HeadlessBuildTaxonomy,
} from './global-ai-generate-headless';
import {
  readChartGeoLevelSwitch,
  readGeographyTaxonomyMismatchTargetId,
} from './global-ai-geo-taxonomy-switch';
import { reportLoopError } from './global-ai-loop-error-telemetry';
import {
  dashboardStreamEventToProgress,
  routePageGenProgress,
} from './global-ai-page-gen-progress';
import { buildReportOfferResumeSeed } from './global-ai-system-seeds';
import {
  createToolProgressSink,
  type ToolEmit,
} from './global-ai-tool-progress';
import { toolStepTitle } from './global-ai-tool-step-titles';
import { runTurnLoop } from './global-ai-turn-loop';
import type { ArtifactCache } from './global-ai-turn-messages';
import type {
  TurnMessage,
  TurnStepRequestBody,
} from './global-ai-turn-step-stream';
import { runTurnStep } from './global-ai-turn-step-stream';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import { fetchHeadlessChartDigest } from './headless-digest/headless-digest-service';
import { hasOfferMarker, type OfferKind } from './offer-marker';
import {
  describeStep,
  emptyTrace,
  endTrace,
  finishStep,
  type ReasoningTrace,
  setStepThinking,
  startStep,
  titleStep,
} from './reasoning-trace';
import type { ChartDataDigest } from './turn-outcome-digest';
import {
  summarizeAppliedChartFilters,
  summarizeUnappliedChartFilters,
  withChartScope,
} from './turn-outcome-digest';

/**
 * How long to wait for the chart-preview runtime snapshot of a freshly
 * rendered chart message before giving up and resolving the model a synthetic
 * empty digest. The CustomDash data providers feed the runtime asynchronously
 * after the chart mounts; 30s covers a cold fetch.
 */
const CHART_SETTLE_TIMEOUT_MS = 30_000;
/** Upper bound on a single apply/update command landing before the loop moves on. */
const COMMAND_COMPLETE_TIMEOUT_MS = 30_000;

/**
 * Upper bound on the page's `NarrationCollector` publishing its widget digests
 * after an `update_current_page` build command is emitted. The bridge streams
 * the page layout, mounts each widget, then publishes on the `pageCollection`
 * channel once every widget's data reporter has settled — a multi-widget cold
 * page can take ~15-20s.
 *
 * MUST exceed the collector's own hard timeout (30s, see `armPageNarration` in
 * the bridge) so the runner keeps waiting until the collector actually
 * publishes, rather than giving up early and handing the model an empty digest
 * set (which would make it narrate the page blind).
 */
const PAGE_COLLECTION_TIMEOUT_MS = 32_000;

const DISCOVERY_NAVIGATION_NO_RETRY_INSTRUCTION =
  'Do not retry apply_filters; explain that the page was not opened.';

function discoveryNavigationFailureMessage(
  target: AssistantDiscoveryTarget,
  result: Extract<NavigateToOptionResult, { notNavigated: true }>
): string {
  if (result.reason === 'no_access') {
    const detail = result.message.trim().replace(/[.!?]+$/, '');
    return `Could not open ${target.name}${
      detail ? `: ${detail}.` : '.'
    } Filters were not applied. ${DISCOVERY_NAVIGATION_NO_RETRY_INSTRUCTION}`;
  }
  if (result.reason === 'cancelled') {
    return `Opening ${target.name} was cancelled. Filters were not applied. ${DISCOVERY_NAVIGATION_NO_RETRY_INSTRUCTION}`;
  }
  return `${target.name} is not available. Filters were not applied. ${DISCOVERY_NAVIGATION_NO_RETRY_INSTRUCTION}`;
}

/**
 * Inputs the runner needs from the drawer that it cannot reconstruct from
 * context/store on its own. These mirror the drawer's existing closures so the
 * loop reuses the exact same machinery the legacy `runAssistantPipeline` does.
 */
export type GlobalAiTurnRunnerDeps = {
  /**
   * Override for the chart-settle budget (default `CHART_SETTLE_TIMEOUT_MS`).
   * Production never sets it; a test that needs the timeout branch lowers it so
   * the wait can expire without a 30s elapse.
   */
  chartSettleTimeoutMs?: number;
  /** Resolve + emit filters for the given path/prompt, mirroring `queueRouteFilterApply`. */
  resolveAndEmitRouteFilters: (args: {
    requestId: string;
    path: string;
    promptForFilters: string;
    entityType: GlobalAssistantEntityType;
    /** Skips the emit (not the resolve) once the turn is aborted. */
    signal?: AbortSignal;
  }) => Promise<TalentDiscoveryAiFilterSearchResponseV2>;
  /**
   * Emit an `applyRouteFilters` command for an ALREADY-resolved tdV2 filter
   * response (no AI re-resolution). The `search_filters` server tool resolved
   * the artifact already; this applies it directly via the bridge's
   * route-filter machinery.
   */
  emitResolvedRouteFilters: (args: {
    requestId: string;
    path: string;
    response:
      | TalentDiscoveryAiFilterSearchResponseV2
      | CompanyDiscoveryAiOutput;
    prompt: string;
    entityType: GlobalAssistantEntityType;
    merge?: boolean;
  }) => void;
  /**
   * Navigate to a `__std__:` candidate id. Resolves with a discriminated
   * result: success carries the landed page display name; failure carries a
   * `reason` (no_access / unknown_page / cancelled) so callers never report a
   * navigation that didn't happen.
   */
  navigateToOptionId: (optionId: string) => Promise<NavigateToOptionResult>;
  /** Emit an `update_current_page` stream command; resolve when it lands. */
  emitUpdateCurrentPage: (requestId: string, prompt: string) => void;
  /**
   * Emit a CREATE `startCustomPageStream` command for the CURRENT draft (no
   * navigation, no new draft) — the build-here path. The drawer already created
   * + navigated to the empty draft at chip-click time; this just emits the
   * build for it. Resolved via the page-collection channel by the runner.
   */
  emitCreateCurrentPage: (requestId: string, prompt: string) => void;
  /**
   * The taxonomy and activity capability the headless `create_custom_page`
   * build resolves its filters under, for the entity it builds (see
   * `AssistantEngine.headlessBuildTaxonomy`).
   */
  headlessBuildTaxonomy: (
    entityType: CustomDashAssistantEntityType
  ) => HeadlessBuildTaxonomy;
  /**
   * Append a `report_offers` message with one chip per `reportType` (the user
   * clicks a chip to launch), mirroring the drawer's report-offer shape.
   * Returns the resolved display names (skipping unresolvable types) for the
   * model's narration.
   */
  offerReport: (
    reportTypes: string[],
    prompt: string
  ) => { reportNames: string[]; messageId?: string };
  /** Current page display name / pathname for the filter digest target. */
  currentPageLabel: () => string;
  /** Default entity type for the current route, used when resolving filter target. */
  inferEntityTypeForPath: (path: string) => GlobalAssistantEntityType;
  /**
   * Build the per-turn step context (taxonomy, current-page, custom-dash
   * edit-mode) from the same sources the legacy `suggest`/`runAssistantPipeline`
   * path used. Called once at the start of each turn. `conversationSummary` is
   * supplied separately by the runner. Fields are omitted when unavailable.
   */
  buildTurnContext: () => TurnStepContextFields;
  /**
   * Called once per client-tool execution with the tool name and the
   * text-answer flag — the engine turns this into the unified
   * `global ai assistant outcome` analytics event (the runner stays
   * analytics-free).
   */
  onToolOutcome?: (toolName: string, meta: { isTextAnswer: boolean }) => void;
};

/**
 * The non-summary fields of the turn-step context, assembled by the engine
 * core's `buildTurnContext` from taxonomy/current-page/custom-dash state and
 * threaded into the loop. Exported for that builder's return type.
 */
export type TurnStepContextFields = Omit<
  NonNullable<TurnStepRequestBody['context']>,
  'conversationSummary'
>;

export type UseGlobalAiTurnRunnerResult = {
  runTurn: (
    prompt: string,
    options?: { suppressReportOffer?: boolean }
  ) => Promise<void>;
};

export function useGlobalAiTurnRunner(
  deps: GlobalAiTurnRunnerDeps
): UseGlobalAiTurnRunnerResult {
  const {
    transcript,
    conversationSummary,
    appendTranscript,
    markTurnStopped,
    updateAssistantMessageText,
    updateReasoningTrace,
    setGenerationStatus,
    mergeChartPreviewRuntime,
    addChartPreviewTimelineEvent,
    turnAbortRef,
  } = useGlobalAIAssistant();

  // Latest transcript for async callbacks that resolve after their closure was
  // created (segment append-dedupe reads this through the ref-tracked Set).
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;

  const depsRef = useRef(deps);
  depsRef.current = deps;

  const runTurn = useCallback(
    async (
      userPrompt: string,
      options?: { suppressReportOffer?: boolean }
    ): Promise<void> => {
      const currentDeps = depsRef.current;

      // Per-turn reasoning trace state.
      const traceId = crypto.randomUUID();
      let trace: ReasoningTrace = emptyTrace(Date.now());
      let traceAppended = false;
      let pendingDescription = '';
      let activeToolStepId: string | null = null;

      // Abort any in-flight turn and open a fresh controller for this one, so
      // closing the drawer (which aborts `turnAbortRef`) stops this turn's SSE
      // and poll waits instead of letting them run to their multi-second
      // timeouts and write to the store/transcript after the drawer is gone.
      turnAbortRef.current?.abort();
      const turnController = new AbortController();
      turnAbortRef.current = turnController;
      const { signal } = turnController;
      // Transcript length before this turn writes anything — the cut point a
      // user stop rewinds to (see `discardStoppedTurnOutput`).
      const outputBaseline = transcriptRef.current.length;

      // --- conversation history -> priorMessages ---
      const bounded = buildBoundedConversationContext({
        transcript: transcriptRef.current,
        summary: conversationSummary,
      });
      const priorMessages: TurnMessage[] = bounded.turns.map((turn) =>
        turn.role === 'user'
          ? { role: 'user', content: turn.content }
          : { role: 'assistant', content: turn.content }
      );

      // --- render_chart: append a single_chart message, await its runtime
      //     snapshot settling, resolve the data digest the data reporter
      //     publishes (or a synthetic empty digest on timeout). ---
      // The turn's most recent single-VALUE answer chart that has a matching
      // standard page — the only kind of chart `offer_standard_page` can
      // target — with that page's catalog id. Gated to those renders (not every
      // render_chart) so a later ordinary chart in the same turn can't hijack
      // the page offer's target.
      let lastStandardPageAnswerChart: StandardPageAnswerChart | undefined;
      // Id of the turn's most recent RENDERED (non-single-value) chart. The
      // model's post-chart narration may carry an offer:page marker; onAnswer
      // stamps it onto this chart so the drawer can render the inline trigger,
      // which is the only standard-page surface.
      let lastRenderedChartId: string | undefined;
      // Target taxonomy of a geography-taxonomy substitution this turn made,
      // read off the `filter_response` artifact root (never off model text —
      // see readGeographyTaxonomyMismatchTargetId). Undefined for the
      // overwhelming majority of turns; when set, `onAnswer` stamps it so the
      // narration's `[…](offer:geo_tax)` phrase becomes a live switch link.
      // The stamp IS the grounding backstop: no mismatch, no stamp, and the
      // marker degrades to plain text through the existing downgrade path.
      let geoTaxMismatchTargetId: number | undefined;
      // True when that target came from a chart's level note: the click then
      // re-asks instead of applying the ask to the page (see `geoTaxReask`).
      let geoTaxFromChart = false;
      const renderChartAndAwaitDigest = async (payload: {
        config: unknown;
        filters?: unknown;
        entityType?: string;
        intentSummary?: string;
        chartSubtitle?: string;
        valueForm?: ChartValueForm;
        standardPagePath?: string;
        postingsMode?: AssistantPostingsMode;
      }): Promise<ChartDataDigest> => {
        const chartMessageId = crypto.randomUUID();
        const payloadEntity = payload.entityType as
          | GlobalAssistantEntityType
          | undefined;
        const capabilityEntityType =
          toSingleChartAssistantCapabilityEntityType(payloadEntity) ??
          'company';
        const primaryFilterId =
          PRIMARY_FILTER_BY_AI_ENTITY[capabilityEntityType];
        const { items } = getValidatedPositionedItemsFromLlmContent(
          payload.config as LLMTabContentForDispatch,
          primaryFilterId
        );
        const firstItem = items[0];
        if (!firstItem) {
          // No renderable chart — return an empty digest so the model can
          // narrate the absence rather than hang on a chart that never mounts.
          return {
            kind: 'chart_data',
            metric: payload.intentSummary ?? 'chart',
            chartType: 'unknown',
            rowCount: 0,
            empty: true,
          };
        }

        // The caption is the model's summary of the ASK, written before the
        // chart compiled, so it names filters the chart may not have applied
        // ("…, excluding California" over a chart that includes it). Shown
        // only when every filter applied; the narration discloses the rest.
        const captionIsAccurate =
          summarizeUnappliedChartFilters(
            payload.filters as
              | TalentDiscoveryAiFilterSearchResponseV2
              | undefined
          ).length === 0;
        appendTranscript({
          id: chartMessageId,
          role: 'assistant',
          kind: 'single_chart',
          item: firstItem,
          filters: payload.filters as
            | TalentDiscoveryAiFilterSearchResponseV2
            | undefined,
          entityType: capabilityEntityType,
          ...(payload.intentSummary && captionIsAccurate
            ? { intentSummary: payload.intentSummary }
            : {}),
          ...(payload.chartSubtitle
            ? { chartSubtitle: payload.chartSubtitle }
            : {}),
          ...(payload.standardPagePath
            ? { standardPagePath: payload.standardPagePath }
            : {}),
          ...(payload.postingsMode
            ? { postingsMode: payload.postingsMode }
            : {}),
          prompt: userPrompt,
        });
        addChartPreviewTimelineEvent(chartMessageId, 'chart message appended');
        lastRenderedChartId = chartMessageId;
        // Deliberately omit `narrationPrompt`: the model narrates the chart as
        // its next text segment, so the auto-narration controller must NOT
        // fire. The chart still renders + reports its data digest.
        mergeChartPreviewRuntime(chartMessageId, { phase: 'mounting' });

        // The chart is already visible; only the narration waits on its data
        // digest. Surface a specific status so the chat isn't an opaque spinner
        // during the settle. Superseded by the model's narration on the next
        // step (or cleared at turn end).
        setGenerationStatus('Reading the chart data…');
        const settled = await waitForChartSettle(
          chartMessageId,
          currentDeps.chartSettleTimeoutMs ?? CHART_SETTLE_TIMEOUT_MS,
          signal
        );
        addChartPreviewTimelineEvent(chartMessageId, 'settle detected');
        if (process.env.NODE_ENV === 'development') {
          // The timeline add above is a synchronous store write, so this read
          // already includes the settle mark.
          const timeline =
            useGlobalAiChartPreviewRuntimeStore.getState().runtimeByMessageId[
              chartMessageId
            ]?.timeline ?? [];
          console.info(
            '[ga-latency]',
            payload.intentSummary ?? 'chart',
            summarizeChartPreviewTimeline(timeline),
            timeline
          );
        }
        if (settled?.dataDigest) {
          // Carry the chart's SCOPE (intentSummary, else subtitle) into the
          // digest title so the grounded narration knows WHAT the chart shows
          // (e.g. "headcount of Asian men at Google"), not just the bare metric,
          // AND the explicit applied-filter labels (`filtersApplied`) so the
          // model has structured ground truth those filters resolved + applied.
          // Without these the model narrates the value as unfiltered and invents
          // "the filter did not resolve" against a single-row result.
          const chartFilters = payload.filters as
            | TalentDiscoveryAiFilterSearchResponseV2
            | undefined;
          return withChartScope(settled.dataDigest, {
            intentSummary: payload.intentSummary,
            chartSubtitle: payload.chartSubtitle,
            valueForm: payload.valueForm,
            filtersApplied: summarizeAppliedChartFilters(chartFilters),
            filtersNotApplied: summarizeUnappliedChartFilters(chartFilters),
          });
        }
        const chartType =
          (firstItem.plot && 'chartType' in firstItem.plot
            ? firstItem.plot.chartType
            : undefined) ?? 'unknown';
        // The chart settled in an ERROR phase (the data layer failed) — emit an
        // error digest so the model narrates a FAILURE rather than "no data".
        // A timeout / non-error settle with no digest stays a synthetic empty.
        if (settled?.phase === 'error') {
          return {
            kind: 'chart_data',
            metric: payload.intentSummary ?? payload.chartSubtitle ?? 'chart',
            chartType: String(chartType),
            rowCount: 0,
            empty: true,
            error: true,
          };
        }
        // Never settled (timeout/abort): the chart may still be loading, so
        // its data is UNKNOWN — mark the synthetic digest `unsettled` so the
        // summary says "didn't finish loading" and NEVER "no data". A chart
        // that genuinely settles empty publishes a real digest with
        // `empty: true` and takes the settled branch above, keeping its
        // honest current wording.
        //
        // `displayed` because this is the VISIBLE path: the card was appended
        // above, before the wait, and the timeout does not unmount it. The
        // render succeeded, so the summary stays non-ERROR and the report
        // ride-along stays armed — only the figures are withheld.
        return {
          kind: 'chart_data',
          metric: payload.intentSummary ?? payload.chartSubtitle ?? 'chart',
          chartType: String(chartType),
          rowCount: 0,
          empty: true,
          unsettled: true,
          displayed: true,
        };
      };

      // --- Headless digest fetch (DP3): the single-value answer path. The
      //     digest comes from the offscreen digest host — no transcript
      //     message, no drawer slot, no chart mount, no settle polling. ---
      const fetchChartDigestHeadless = async (payload: {
        config: unknown;
        filters?: unknown;
        entityType?: string;
        intentSummary?: string;
        chartSubtitle?: string;
        valueForm?: ChartValueForm;
        postingsMode?: AssistantPostingsMode;
      }): Promise<ChartDataDigest> => {
        // A new single-value answer supersedes the previous one as the page
        // offer's target. It becomes the target again only if
        // appendAnswerContextChart links it, so an unlinked answer can't send
        // the offer to an earlier answer's filters.
        lastStandardPageAnswerChart = undefined;
        const payloadEntity = payload.entityType as
          | GlobalAssistantEntityType
          | undefined;
        const capabilityEntityType =
          toSingleChartAssistantCapabilityEntityType(payloadEntity) ??
          'company';
        const runtimeEntityType: CustomDashAssistantEntityType =
          payloadEntity === 'industry' ? 'industry' : capabilityEntityType;
        const primaryFilterId =
          PRIMARY_FILTER_BY_AI_ENTITY[capabilityEntityType];
        const { items } = getValidatedPositionedItemsFromLlmContent(
          payload.config as LLMTabContentForDispatch,
          primaryFilterId
        );
        const firstItem = items[0];
        if (!firstItem) {
          // Same contract as renderChartAndAwaitDigest: empty digest, never hang.
          return {
            kind: 'chart_data',
            metric: payload.intentSummary ?? 'chart',
            chartType: 'unknown',
            rowCount: 0,
            empty: true,
          };
        }
        setGenerationStatus('Looking up the figure…');
        const digest = await fetchHeadlessChartDigest(
          {
            item: firstItem,
            filters: payload.filters as
              | TalentDiscoveryAiFilterSearchResponseV2
              | undefined,
            entityType: runtimeEntityType,
            ...(payload.postingsMode
              ? { postingsMode: payload.postingsMode }
              : {}),
          },
          { signal }
        );
        // Same scope enrichment the legacy path applies (intent + applied-
        // filter labels ground the narration).
        const chartFilters = payload.filters as
          | TalentDiscoveryAiFilterSearchResponseV2
          | undefined;
        return withChartScope(digest, {
          intentSummary: payload.intentSummary,
          chartSubtitle: payload.chartSubtitle,
          valueForm: payload.valueForm,
          filtersApplied: summarizeAppliedChartFilters(chartFilters),
          filtersNotApplied: summarizeUnappliedChartFilters(chartFilters),
        });
      };

      // Append the card-less single_chart transcript message that carries the
      // offer:page wiring (standardPagePath + filters + prompt — the drawer's
      // inline-link click reads them). answerAsText messages are NEVER mounted
      // (excluded from chart-preview liveness), so this is pure metadata — the
      // digest itself came from the headless service. Success path only.
      const appendAnswerContextChart = (payload: {
        config: unknown;
        filters?: unknown;
        entityType?: string;
        intentSummary?: string;
        standardPagePath: string;
      }): void => {
        const payloadEntity = payload.entityType as
          | GlobalAssistantEntityType
          | undefined;
        const capabilityEntityType =
          toSingleChartAssistantCapabilityEntityType(payloadEntity) ??
          'company';
        const primaryFilterId =
          PRIMARY_FILTER_BY_AI_ENTITY[capabilityEntityType];
        const { items } = getValidatedPositionedItemsFromLlmContent(
          payload.config as LLMTabContentForDispatch,
          primaryFilterId
        );
        const firstItem = items[0];
        if (!firstItem) return;
        const chartMessageId = crypto.randomUUID();
        appendTranscript({
          id: chartMessageId,
          role: 'assistant',
          kind: 'single_chart',
          item: firstItem,
          filters: payload.filters as
            | TalentDiscoveryAiFilterSearchResponseV2
            | undefined,
          entityType: capabilityEntityType,
          ...(payload.intentSummary
            ? { intentSummary: payload.intentSummary }
            : {}),
          standardPagePath: payload.standardPagePath,
          answerAsText: true,
          prompt: userPrompt,
        });
        lastStandardPageAnswerChart = {
          id: chartMessageId,
          pageOptionId: getCurrentPageOptionId(payload.standardPagePath),
        };
      };

      // --- create_custom_page (BUILD-HERE mode): emit a CREATE page-gen for
      //     the CURRENT draft (the chip click already navigated us to the empty
      //     draft) and collect the built page's widget digests so the model
      //     narrates a grounded CREATE summary of the actual widgets. Shares
      //     `emitPageGenAndCollect` with `updateCurrentPage`; only the emitted
      //     command (mode:'create') and the status strings differ. ---
      const buildCustomPageHereAndCollect = (prompt: string, emit?: ToolEmit) =>
        emitPageGenAndCollect({
          // Read the LIVE deps ref, not the turn-start snapshot: the drawer impl
          // closes over `window.location.pathname` to target the current draft.
          emitCommand: (requestId) =>
            depsRef.current.emitCreateCurrentPage(requestId, prompt),
          emit,
          signal,
          preEmitStatus: 'Designing your dashboard…',
          onSettleStart: () =>
            setGenerationStatus('Analyzing the page results…'),
        });

      const handles: ClientToolHandles = {
        getCurrentPageOptionId: () =>
          getCurrentPageOptionId(depsRef.current.currentPageLabel()),
        navigateToOption: (optionId) =>
          currentDeps.navigateToOptionId(optionId),
        // The `navigate` tool is terminal: it never drives the router. Resolve
        // the optionId to a destination name and append a confirmation chip
        // carrying the user's prompt so a click can resume any chained task.
        // The chip's click handler performs the guarded navigation.
        requestNavigateConfirm: (
          optionId,
          withContinuation = true,
          grounded?: {
            resolvedFilters: TalentDiscoveryAiFilterSearchResponseV2;
            entityType?: GlobalAssistantEntityType;
          }
        ) => {
          const match = getStandardPageMatchForOptionId(optionId, {});
          if (!match) {
            return { unknownPage: true } as const;
          }
          // ALWAYS append the nav_confirm chip — home and off-home alike.
          // Nothing auto-navigates: the chip / inline trigger is fired by an
          // explicit user click everywhere (home behaves exactly like off-home).
          // A click performs the guarded navigation and runs any chained task,
          // so a compound request (open page + filter) still completes via the
          // continuationPrompt. Preserving continuationPrompt / withContinuation
          // here keeps that behavior.
          const messageId = crypto.randomUUID();
          appendTranscript({
            id: messageId,
            role: 'assistant',
            kind: 'nav_confirm',
            optionId,
            pageName: match.name,
            // When continuation is suppressed (offer_page_or_custom's page chip),
            // omit the prompt so the chip just navigates on click — no resumed
            // turn that would try to answer this prompt by filtering the page.
            ...(withContinuation ? { continuationPrompt: userPrompt } : {}),
            // Data-grounded offer: carry the answer's exact resolved artifact
            // so the click applies it on the destination (text=link contract).
            ...(grounded
              ? {
                  filters: grounded.resolvedFilters,
                  ...(grounded.entityType
                    ? { entityType: grounded.entityType }
                    : {}),
                }
              : {}),
          });
          return { pageName: match.name, messageId } as const;
        },
        applyFilters: async (filterResponse, target, merge) => {
          const requestId = crypto.randomUUID();
          // Only current_page is relative. Explicit Discovery targets own their
          // destination even when the user's current page is unrelated.
          const discoveryTarget = getDiscoveryTarget(target);
          if (target !== 'current_page' && !discoveryTarget) {
            throw new Error(`Unsupported filter target: ${target}`);
          }
          const currentPath = currentDeps.currentPageLabel();
          const path = discoveryTarget?.path ?? currentPath;

          // Navigate to the discovery surface first if we are not already there.
          if (
            discoveryTarget &&
            !globalAssistantPathsMatch(currentPath, discoveryTarget.path)
          ) {
            const navigationResult = await currentDeps.navigateToOptionId(
              discoveryTarget.optionId
            );
            if ('notNavigated' in navigationResult) {
              throw new Error(
                discoveryNavigationFailureMessage(
                  discoveryTarget,
                  navigationResult
                )
              );
            }
          }

          // The navigation above spans the event loop, so the turn can be
          // stopped mid-flight. Emitting past that point mutates the
          // DESTINATION page's filters for a turn the user cancelled — and
          // unlike a transcript write, `markTurnStopped` cannot rewind it (it
          // only truncates the message array). The tool's own post-await guard
          // cannot cover this: the side effect is inside this handle, not
          // after it.
          if (signal.aborted) {
            throw new DOMException('apply_filters aborted', 'AbortError');
          }

          // Read the LIVE deps ref, not the turn-start snapshot: when this turn
          // navigated mid-loop (navigate -> search_filters -> apply_filters) a
          // re-render swapped the page-derived closure, so the turn-start
          // `currentDeps.inferEntityTypeForPath` would infer for the page we LEFT.
          const entityType =
            discoveryTarget?.entityType ??
            depsRef.current.inferEntityTypeForPath(path);
          if (isFilterResponse(filterResponse)) {
            // The `search_filters` server tool already resolved the filters
            // into this artifact. Apply it directly through the bridge's
            // route-filter machinery WITHOUT re-running AI resolution.
            currentDeps.emitResolvedRouteFilters({
              requestId,
              path,
              response: filterResponse,
              prompt: userPrompt,
              entityType,
              merge,
            });
            // Optimistic (DP4): the digest is built from the artifact, not
            // from page completion — the bridge consumes the command when the
            // target page mounts, and both failure modes (TTL expiry, bridge
            // apply error) surface in-chat via the `filterApplyFailure`
            // channel. Not
            // waiting saves the page-apply latency (up to COMMAND_TTL_MS on a
            // missed apply) from the turn.
            return { response: filterResponse, targetPage: path };
          }
          // Defensive fallback (shouldn't happen): no usable artifact, so
          // resolve from the USER PROMPT — never from `target`. The AWAIT here
          // is the RESOLUTION (it produces the digest content); only the page
          // completion wait is skipped, same as the artifact path above.
          const response = await currentDeps.resolveAndEmitRouteFilters({
            requestId,
            path,
            promptForFilters: userPrompt,
            entityType,
            signal,
          });
          return { response, targetPage: path };
        },
        renderChartAndAwaitDigest,
        fetchChartDigest: fetchChartDigestHeadless,
        appendAnswerContextChart,
        requestCreateCustomPage: async (
          prompt,
          entityType,
          emit,
          forceOffer,
          groundedFilters
        ) => {
          const requestedEntity = entityType as
            | GlobalAssistantEntityType
            | undefined;
          const cdEntityType: CustomDashAssistantEntityType =
            toCustomDashAssistantEntityType(requestedEntity) ?? 'company';
          // Consume-once: a build-here arm belongs to the single click that
          // armed it (the "Build the dashboard" chip created an empty draft,
          // navigated to it, and armed the signal for the resumed turn).
          // Consuming UNCONDITIONALLY — including on the pure-OFFER
          // forceOffer path — retires any stale arm as a side effect of the
          // slot's semantics, so no separate stale-arm defense is needed here.
          // An arm whose turn died (error/stop) before reaching this tool is
          // already gone: `endTurn` drops it at the turn's idle transition, so
          // a plain offer can never silently build. See the
          // GlobalAiPendingContinuation docblock.
          const buildHere = consumeContinuation('build_here');
          if (!forceOffer && buildHere) {
            // BUILD-HERE: run the CREATE page-gen on THIS draft (no nav, no
            // new draft) and return widget digests for a grounded CREATE
            // summary.
            const { ok, digests, expectedCount, failureReason } =
              await buildCustomPageHereAndCollect(prompt, emit);
            // expectedCount must ride along or the create_custom_page tool's
            // partial-aware narration branch never fires and a 3-of-6-settled
            // cold build gets the absolute "ONLY charts" wording (Greptile).
            return {
              built: true as const,
              ok,
              digests,
              expectedCount,
              failureReason,
            };
          }
          // RESOLVE-FIRST OFFER (Phase C, meeting 2026-07-08): for the explicit
          // create_custom_page offer, run the page-gen stream HEADLESSLY now —
          // the streamed thinking rides the existing progress sink into the
          // activity lane — and attach the finished layout to the Build chip.
          // The click then seeds a draft with the layout and navigates: no
          // empty draft, no on-page build, no resumed turn. Generation failure
          // or a clarification-questions event falls back to the LEGACY
          // confirm-navigate-resume chip (progressive enhancement, never a
          // dead end). offer_page_or_custom (forceOffer) stays legacy: its
          // build chip is a secondary option — pre-generating a full page for
          // every partial-fit offer would tax every causal ask.
          let generated:
            | {
                content: LLMTabContentForDispatch;
                filters?: TalentDiscoveryAiFilterSearchResponseV2;
              }
            | undefined;
          if (!forceOffer) {
            generated =
              (await generateDashboardHeadless({
                prompt,
                entityType: cdEntityType,
                conversation: toConversationPayload(bounded),
                taxonomy: depsRef.current.headlessBuildTaxonomy(cdEntityType),
                onProgress: (event) => {
                  const progress = dashboardStreamEventToProgress(event);
                  if (progress) {
                    emit?.(routePageGenProgress(progress));
                  }
                },
                signal,
              })) ?? undefined;
          }
          // Terminal OFFER tool: append the build-confirm chip and end the turn.
          // Store the FULL original user prompt (the message that triggered this
          // turn), NOT the tool's cleaned `prompt` argument — for the LEGACY
          // path the chip click navigates to an empty draft and RESUMES the
          // turn seeded with this prompt, and the resume must carry the user's
          // full "build a dashboard" intent so the model rebuilds the page
          // (calls create_custom_page in BUILD-HERE mode) rather than
          // re-routing a stripped prompt to a single chart.
          const messageId = crypto.randomUUID();
          appendTranscript({
            id: messageId,
            role: 'assistant',
            kind: 'create_page_confirm',
            prompt: userPrompt,
            entityType: cdEntityType,
            ...(generated ? { generated } : {}),
            // Data-grounded offer artifact — the lazy build's landing-filter
            // fallback (see the create_page_confirm.filters docblock).
            ...(groundedFilters ? { filters: groundedFilters } : {}),
          });
          return { offered: true as const, messageId };
        },
        // Shares `emitPageGenAndCollect` with the BUILD-HERE create: the
        // collector wait is critical for the confirm-navigate-resume build,
        // where this tool builds out the empty draft (otherwise it narrates
        // the page blind).
        updateCurrentPage: (prompt, emit) =>
          emitPageGenAndCollect({
            // Read the LIVE deps ref, not the turn-start snapshot: the drawer
            // impl closes over React Router's `location.pathname` to pick the
            // entity type + target page. A mid-turn navigation re-renders and
            // swaps that closure, so the turn-start snapshot would target the
            // page we LEFT.
            emitCommand: (requestId) =>
              depsRef.current.emitUpdateCurrentPage(requestId, prompt),
            emit,
            signal,
          }),
        // LIVE ref, not the turn-start snapshot: `offerReport` closes over the
        // engine's `reportBuilderConfig`, which is the account's
        // `GetReportConfig` query — and that query only fires when the engine
        // mounts (the assistant opening). A turn started within a few hundred
        // ms of that mount snapshots a deps object whose config is still `{}`,
        // so every requested reportType fails to resolve and the user is told
        // no report matched, seconds after the config actually landed. Same
        // stale-snapshot class as the three page-derived deps above.
        offerReport: async (reportTypes, prompt) =>
          depsRef.current.offerReport(reportTypes, prompt),
        // Append the offer tool's `narration` param as a normal assistant text
        // message. The drawer renders `text` messages through
        // `parseOfferSegments`, so the `[…](offer:nav|build)` markers become
        // inline trigger spans and `turnNarrationHasOffer` suppresses the
        // redundant chip. Reuses the same `appendTranscript` the final answer
        // uses (onAnswer), so ordering/rendering match a model narration.
        appendAssistantText: (text, offerTargets) => {
          // A tool's narration can carry the geography-switch marker too, and
          // several do: `navigate` is TERMINAL, so on that path this text —
          // not `onAnswer` — is the whole turn the user reads. Stamping only
          // there left "Hartford County resolved to Hartford" with its offer
          // silently downgraded to prose, which reads as the assistant simply
          // not having one.
          //
          // Same grounding rule as `onAnswer`: the target comes from the
          // turn's own filter_response artifact, never from the model's words,
          // and only when this turn actually SENT a geography taxonomy.
          const geoTaxStamp =
            geoTaxMismatchTargetId !== undefined &&
            typeof context.geoTax === 'number' &&
            hasOfferMarker(text, 'geo_tax')
              ? { geo_tax: String(geoTaxMismatchTargetId) }
              : undefined;
          const stamps =
            offerTargets || geoTaxStamp
              ? { ...(offerTargets ?? {}), ...(geoTaxStamp ?? {}) }
              : undefined;
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'text',
            text,
            ...(stamps ? { offerTargets: stamps } : {}),
            // The ask that earned the offer, so clicking it re-resolves THIS
            // request rather than whatever was asked most recently.
            ...(geoTaxStamp ? { prompt: userPrompt } : {}),
            ...(geoTaxStamp && geoTaxFromChart ? { geoTaxReask: true } : {}),
          });
        },
        getLastStandardPageAnswerChart: () => lastStandardPageAnswerChart,
        askUser: (question, options) => {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'clarification',
            text: question,
            ...(options
              ? { clarificationOptions: options as GlobalAssistantEntityType[] }
              : {}),
          });
        },
      };

      const toolDeps = createClientToolDeps(handles);

      // Read the live deps ref, not the turn-start snapshot: `buildTurnContext`
      // reads `location.pathname`, which a mid-turn navigation can swap.
      const contextFields = depsRef.current.buildTurnContext();
      const context: NonNullable<TurnStepRequestBody['context']> = {
        ...contextFields,
        ...(bounded.summary ? { conversationSummary: bounded.summary } : {}),
      };
      const hasContext = Object.keys(context).length > 0;

      /**
       * Rebuilt for each step. The snapshot above is right for the progress
       * sink; a `current_page` read after `apply_filters` navigated must see
       * the new route (see `context` on the loop params).
       */
      const buildStepContext = ():
        | NonNullable<TurnStepRequestBody['context']>
        | undefined => {
        const stepContext: NonNullable<TurnStepRequestBody['context']> = {
          ...depsRef.current.buildTurnContext(),
          ...(bounded.summary ? { conversationSummary: bounded.summary } : {}),
        };
        return Object.keys(stepContext).length > 0 ? stepContext : undefined;
      };

      // Per-turn progress sink: lets a running tool stream `status` ticks to
      // the activity lane and `narrate` checkpoints to the chat. No-op until a
      // tool emits (a later task wires create_custom_page).
      const progress = createToolProgressSink({
        userGoal: userPrompt,
        ...(hasContext ? { context } : {}),
        signal,
        newId: () => crypto.randomUUID(),
        appendTranscript,
        updateAssistantMessageText,
        setGenerationStatus,
        onThinking: (text) => {
          if (!activeToolStepId) return;
          trace = setStepThinking(trace, activeToolStepId, text);
          updateReasoningTrace(traceId, trace);
        },
      });

      // Kick off the deterministic report selector in parallel with the turn
      // loop. We never await this here, so it adds no latency to the turn; we
      // resolve it only after the loop produces an analytical outcome below.
      const reportSelectionPromise = postGlobalAssistantSelectReport({
        prompt: userPrompt,
      }).catch(() => null);

      // Per-turn flags driving the post-loop report ride-along. We offer a
      // matching report only when the turn produced an analytical outcome
      // (custom page or chart) AND the model did not already offer one itself.
      let analyticalOutcomeRan = false;
      let modelOfferedReport = false;

      setGenerationStatus('Thinking…');
      try {
        await runTurnLoop({
          userPrompt,
          priorMessages,
          signal,
          context: buildStepContext,
          postStep: runTurnStep,
          executeTool: (name, input, artifacts) => {
            // A single-value `answerAsText` render is a plain text reply, not a
            // chart/page deliverable — it must NOT trigger the post-loop report
            // ride-along.
            const isTextAnswer = readAnswerAsText(name, input, artifacts);
            const isAnalyticalTool = isAnalyticalToolCall(name, isTextAnswer);
            if (name === 'offer_report') {
              modelOfferedReport = true;
            }
            const result = executeClientTool(
              name,
              input,
              artifacts,
              toolDeps,
              progress.emit,
              // Threaded into ClientToolContext so awaiting tools bail without
              // side effects once this turn is aborted (stop / unmount / new
              // turn) — the digest layer resolves rather than rejects on abort.
              signal
            ).then((toolResult) => {
              // A turn-aborted no-op resolves like any other result, but the
              // user saw nothing — so it is excluded here for the same reason a
              // throw is, and must not arm the ride-along below either.
              if (isTurnCancelledResult(toolResult)) {
                return toolResult;
              }
              // Outcome counts only once the tool RESOLVES — a throw means the
              // user never saw the chart/offer, and recording it would
              // overstate assistant output in the funnel.
              currentDeps.onToolOutcome?.(name, { isTextAnswer });
              // Arm the ride-along on the RESULT, not the tool name: a chart
              // whose data layer failed still returns a result, and offering
              // "here's a matching report" against a turn whose chart visibly
              // errored reads as the assistant ignoring its own failure.
              //
              // Which of these can actually fail: `render_chart` (a failed
              // settle, or a missing config artifact) and `create_custom_page`
              // in BUILD-HERE mode (the expiry/timeout branches). What cannot:
              // `offer_page_or_custom`, and `create_custom_page` in its default
              // OFFER mode — both succeed by merely EMITTING the offer, since
              // the build happens later, on click. Those keep today's behaviour.
              if (isAnalyticalTool && !isClientToolFailure(toolResult)) {
                analyticalOutcomeRan = true;
              }
              return toolResult;
            });
            // Close the progress sink once an analytical tool resolves: it is the
            // turn's last narration-producing step, so any narrate still in
            // flight must be dropped rather than land after the model's final
            // response (chat ordering).
            //
            // Deliberately NOT `isAnalyticalTool`: this list omits the
            // `isTextAnswer` carve-out on purpose. A single-value render is not
            // an analytical OUTCOME (no chart is shown, so no report is
            // offered), but it is still a narration-producing step whose sink
            // must close, or a late `narrate` lands after the final answer.
            if (
              name === 'create_custom_page' ||
              name === 'offer_page_or_custom' ||
              name === 'render_chart'
            ) {
              return result.finally(() => progress.close());
            }
            return result;
          },
          onArtifact: ({ kind, payload }) => {
            if (kind === 'chart_config') {
              // A chart drawn at a level the user's geography view lacks
              // offers the same switch as a substituted place.
              const levelSwitch = readChartGeoLevelSwitch(payload);
              if (levelSwitch) {
                geoTaxMismatchTargetId = levelSwitch.otherTaxonomyId;
                geoTaxFromChart = true;
              }
              return;
            }
            if (kind !== 'filter_response') return;
            const targetId = readGeographyTaxonomyMismatchTargetId(payload);
            // Last mismatch wins: a turn can resolve filters more than once,
            // and the narration disclosing the substitution follows the LAST
            // resolution the model saw.
            if (targetId !== undefined) {
              geoTaxMismatchTargetId = targetId;
              geoTaxFromChart = false;
            }
          },
          onText: () => {
            // Intentionally a no-op: loop narration is rendered as grey step
            // descriptions in the reasoning trace (via onStepDescribe/onStepTool).
            // The single black answer message is produced by onAnswer. Rendering
            // narration here too caused the grey step + black text duplication.
          },
          onStatus: (content) => setGenerationStatus(content),
          onError: (message) => {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'error',
              text: message,
            });
          },
          onStepStart: (_roundId) => {
            // Lazy-create the trace element on the turn's first step event.
            // Steps are created per-TOOL (in onStepTool), not per-round, so
            // they render in execution order even when a round runs several
            // server tools before its client tool.
            if (!traceAppended) {
              trace = emptyTrace(Date.now());
              appendTranscript({
                id: traceId,
                role: 'assistant',
                kind: 'reasoning_trace',
                trace,
              });
              traceAppended = true;
            }
          },
          onStepDescribe: (_roundId, text) => {
            // Buffer the round's narration; it becomes the description of the
            // round's FIRST tool step (cleared once consumed).
            pendingDescription = text;
          },
          onStepTool: (stepId, name, input, artifacts) => {
            trace = startStep(trace, stepId);
            // A single-value answer renders no chart, so relabel render_chart's
            // step from "Rendering the chart".
            const answerAsText = readAnswerAsText(name, input, artifacts);
            trace = titleStep(
              trace,
              stepId,
              toolStepTitle(name, input, { answerAsText })
            );
            // `ask_user` ends the turn WITHOUT firing onAnswer (which is what
            // normally clears the buffered narration), so a pre-question "I'll
            // generate…" clause would otherwise stick to the ask_user step as a
            // stale grey description while the actual outcome is a clarifying
            // question — the question itself is already rendered as its own
            // clarification message. Skip the description for ask_user;
            // always clear the buffer so it can't bleed into the next round.
            if (pendingDescription && name !== 'ask_user') {
              trace = describeStep(trace, stepId, pendingDescription);
            }
            pendingDescription = '';
            activeToolStepId = stepId;
            updateReasoningTrace(traceId, trace);
          },
          onStepEnd: (stepId, status) => {
            trace = finishStep(trace, stepId, status);
            if (activeToolStepId === stepId) activeToolStepId = null;
            updateReasoningTrace(traceId, trace);
          },
          onAnswer: (roundId, text) => {
            // The narration is the final answer, not a step. Only touch the trace
            // if one was actually appended this turn — a text-only turn (no tool)
            // never created a reasoning_trace, so skip the mutations to avoid a
            // spurious re-render. When a terminal tool (offer_report/ask_user)
            // ended the turn, its step carries the narration as a description;
            // clear it so the grounded answer isn't duplicated.
            if (traceAppended) {
              trace = endTrace(trace, Date.now());
              trace = describeStep(trace, roundId, '');
              updateReasoningTrace(traceId, trace);
            }
            // The round's narration was the answer, not a step description —
            // clear pending so it doesn't bleed into the next turn.
            pendingDescription = '';
            // Render the final answer as a normal text message. onAnswer fires at
            // most once per turn, so a plain append needs no dedup guard. When the
            // turn rendered a chart and the narration carries post-chart offer
            // markers, stamp them onto that chart so the drawer wires the inline
            // triggers. Marker omitted → no stamp → no page offer at all: the
            // inline trigger is the only standard-page surface, so the narration
            // is the single thing that decides whether a page is offered.
            const answerOfferTargets: Partial<Record<OfferKind, string>> = {};
            if (lastRenderedChartId) {
              for (const kind of ['page', 'filters'] as const) {
                if (hasOfferMarker(text, kind)) {
                  answerOfferTargets[kind] = lastRenderedChartId;
                }
              }
            }
            // The geography-taxonomy switch link. Unlike the chart offers this
            // stamp is not a message id but the TARGET TAXONOMY the artifact
            // named — the drawer reads it back rather than trusting the phrase
            // the model wrote, so a marker the model emits without a real
            // substitution behind it can never re-key the account.
            //
            // Gated on the turn having actually SENT a geography taxonomy: the
            // backend detects the mismatch either way, but an account-wide
            // taxonomy switch only makes sense for a turn resolved under the
            // user's own `context.geoTax`.
            if (
              geoTaxMismatchTargetId !== undefined &&
              typeof context.geoTax === 'number' &&
              hasOfferMarker(text, 'geo_tax')
            ) {
              answerOfferTargets.geo_tax = String(geoTaxMismatchTargetId);
            }
            appendTranscript({
              id: roundId,
              role: 'assistant',
              kind: 'text',
              text,
              ...(Object.keys(answerOfferTargets).length
                ? { offerTargets: answerOfferTargets }
                : {}),
              // Carry THIS turn's ask alongside a geo_tax stamp: clicking the
              // switch re-resolves filters, and must re-resolve the ask that
              // earned the offer rather than whichever prompt is latest when
              // the user gets round to clicking an older message.
              ...(answerOfferTargets.geo_tax !== undefined
                ? { prompt: userPrompt }
                : {}),
              ...(answerOfferTargets.geo_tax !== undefined && geoTaxFromChart
                ? { geoTaxReask: true }
                : {}),
            });
          },
          onToolCall: () => {
            // Bookkeeping hook — no-op for now.
          },
        });

        // Report ride-along (product invariant): when the turn
        // produced an analytical outcome and the model did not already offer a
        // report, a matching prebuilt report is ALWAYS offered. Instead of
        // appending bare cards deterministically, resume the loop with a
        // seeded instruction so the MODEL makes the validated `offer_report`
        // call — the offer then renders as explained narration with inline
        // [Name](offer:report) links. On any failure (validation, refusal,
        // network), fall back to the deterministic card append so the
        // invariant holds. `select-report` is already confidence-gated on the
        // BE, so empty offers simply render nothing. Placed inside the `try`
        // after the loop so a thrown turn skips it.
        if (
          analyticalOutcomeRan &&
          !modelOfferedReport &&
          !options?.suppressReportOffer
        ) {
          const selection = await reportSelectionPromise;
          const offers = selection?.offers ?? [];
          const reportTypes = offers.map((o) => o.reportType);
          // Skip the ride-along if the turn was aborted (drawer closed) — don't
          // append a report offer to a turn the user walked away from.
          if (reportTypes.length > 0 && !signal.aborted) {
            // Silent loop resume: let the MODEL offer the report so the offer
            // arrives as explained narration with an inline [Name](offer:report)
            // link (validated by the offer_report schema), instead of a bare
            // card. One extra round; the main answer has already rendered.
            const reportLines = offers
              .map(
                (o) =>
                  `${o.reportName ?? o.reportType}${o.description ? ` — ${o.description}` : ''} (reportType: ${o.reportType})`
              )
              .join('; ');
            const seed = buildReportOfferResumeSeed({
              reportLines,
              reportTypes,
              userPrompt,
            });
            let resumedOfferSucceeded = false;
            setGenerationStatus('Finding a matching report…');
            try {
              await runTurnLoop({
                userPrompt: seed,
                priorMessages,
                signal,
                context: buildStepContext,
                postStep: runTurnStep,
                // offer_report is a single terminal action here. A SUCCESSFUL
                // call ends the loop via endsTurn; but the zero-chips path
                // returns a NON-terminal "No matching report" result, and since
                // this resume hard-restricts every other tool to an ERROR, the
                // model can only re-call offer_report — looping to the default
                // max steps (observed 5× on a causal answer, blowing latency).
                // Cap at 2: one real attempt plus one slack round, after which
                // the deterministic card fallback below fires.
                maxSteps: 2,
                // HARD-restrict the resume to offer_report: a non-compliant
                // round must not be able to render charts, navigate, or build
                // from inside this bolt-on step.
                executeTool: async (name, input, artifacts) => {
                  if (name !== 'offer_report') {
                    return {
                      resultForModel: `ERROR: only offer_report is available in this step — call offer_report as instructed.`,
                    };
                  }
                  const result = await executeClientTool(
                    name,
                    input,
                    artifacts,
                    toolDeps,
                    undefined,
                    signal
                  );
                  if (result.endsTurn) {
                    resumedOfferSucceeded = true;
                  }
                  return result;
                },
                onText: () => {
                  // Stray streamed text is dropped — the offer_report narration
                  // param is this resume's only user-facing render.
                },
                onAnswer: () => {
                  // Same: never render the resume's free text.
                },
                onStatus: () => {
                  // Keep the single 'Finding a matching report…' status.
                },
                onError: () => {
                  // Swallow; the deterministic card fallback below covers it.
                },
                onToolCall: () => {
                  // Bookkeeping hook — no-op (matches the main loop).
                },
              });
            } catch {
              // Fall through to the deterministic fallback.
            }
            // Product invariant: a matching report is ALWAYS offered. If the
            // resume didn't produce the offer (validation failed twice, model
            // refused, network error), append the plain cards exactly as the
            // pre-resume ride-along did.
            if (!resumedOfferSucceeded && !signal.aborted) {
              depsRef.current.offerReport(reportTypes, userPrompt);
            }
          }
        }
      } catch (err) {
        if (signal.aborted) {
          // Never an error bubble — an abort is not a failure, and one would
          // surface as a spurious error when the drawer reopens. Record the
          // cancellation for telemetry only; the user-stop rewind lives in
          // `finally` (an abort doesn't always throw — see the note there).
          void reportLoopError({
            source: 'sse_envelope',
            errorClass: 'user_cancelled',
          });
        } else {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'error',
            text: 'Sorry — I ran into a problem completing that. Please try again.',
          });
          void reportLoopError({
            source: 'sse_envelope',
            errorClass:
              err instanceof Error && /cancel/i.test(err.message)
                ? 'user_cancelled'
                : 'unexpected',
          });
        }
      } finally {
        // Ensure the reasoning trace always closes — runTurnLoop returns
        // without onAnswer when a tool ends the turn (offer_report/ask_user),
        // on error/max-steps, on abort, or on a throw. Without this the UI
        // would render "Working…" forever.
        if (traceAppended && trace.endedAt === undefined) {
          trace = endTrace(trace, Date.now());
          updateReasoningTrace(traceId, trace);
        }
        // Rewind an explicit user stop HERE, not in `catch`: an abort often
        // returns rather than throws (a terminal tool ends the loop, a
        // settle/collection poll bails on the signal), and those paths skip
        // `catch` entirely — which is how a half-built page offer survived a
        // stop and stayed clickable. Runs after the trace close above so the
        // trace we keep is already terminal. Drawer-close / hydrate / unmount
        // aborts carry no reason, so they stay silent.
        if (signal.aborted && signal.reason === USER_STOP_ABORT_REASON) {
          markTurnStopped(outputBaseline);
        }
        // Only clear the shared ref if it still points at THIS turn — a newer
        // turn may have already replaced it.
        if (turnAbortRef.current === turnController) {
          turnAbortRef.current = null;
        }
        setGenerationStatus(null);
      }
    },
    [
      appendTranscript,
      markTurnStopped,
      conversationSummary,
      mergeChartPreviewRuntime,
      addChartPreviewTimelineEvent,
      setGenerationStatus,
      updateAssistantMessageText,
      updateReasoningTrace,
      turnAbortRef,
    ]
  );

  return { runTurn };
}

function isFilterResponse(
  value: unknown
): value is TalentDiscoveryAiFilterSearchResponseV2 | CompanyDiscoveryAiOutput {
  // Accept either the TD v2 shape (`filters` + root `relevantPromptText`) or
  // the screener `CompanyDiscoveryAiOutput` shape, which has `filters` but no
  // root `relevantPromptText` — instead it carries `unknownFilters` and nested
  // screener markers (`primary_selector` / `segments` / `entity_info_selectors`)
  // on `value.filters`. Without this branch a resolved screener artifact would
  // wrongly fall through to the prompt re-resolve fallback.
  if (typeof value !== 'object' || value === null || !('filters' in value)) {
    return false;
  }
  if ('relevantPromptText' in value || 'unknownFilters' in value) {
    return true;
  }
  const filters = (value as { filters: unknown }).filters;
  if (typeof filters !== 'object' || filters === null) {
    return false;
  }
  return (
    'primary_selector' in filters ||
    'segments' in filters ||
    'entity_info_selectors' in filters
  );
}

/**
 * Whether a tool call is a single-value `answerAsText` render — a plain text
 * reply, not a chart/page deliverable. The flag rides on the chart_config
 * artifact the `render_chart` call points at, so it can only be read through
 * the artifact cache.
 */
function readAnswerAsText(
  name: string,
  input: unknown,
  artifacts: ArtifactCache | undefined
): boolean {
  if (
    name !== 'render_chart' ||
    input === null ||
    typeof input !== 'object' ||
    !('configArtifactId' in input)
  ) {
    return false;
  }
  const artifact = artifacts?.get(
    String((input as { configArtifactId: unknown }).configArtifactId)
  ) as { answerAsText?: boolean } | undefined;
  return artifact?.answerAsText === true;
}

/**
 * Whether the tool call produces an analytical OUTCOME (custom page or chart)
 * — the trigger for the post-loop report ride-along. A single-value
 * `answerAsText` render is carved out: it renders no chart, so no report is
 * offered against it.
 */
function isAnalyticalToolCall(name: string, isTextAnswer: boolean): boolean {
  return (
    name === 'create_custom_page' ||
    name === 'offer_page_or_custom' ||
    (name === 'render_chart' && !isTextAnswer)
  );
}

/**
 * Shared emit-and-collect core for the two page-gen client tools
 * (`create_custom_page` in BUILD-HERE mode and `update_current_page`): stream
 * the build's page-gen progress to the activity lane, emit the command, await
 * its completion, then await the page's collected widget digests so the model
 * narrates a grounded summary of the ACTUAL widgets instead of a blind
 * template.
 *
 * The collection channel is baselined BEFORE emitting: the bridge's
 * `NarrationCollector` publishes on the `pageCollection` channel once the
 * built page's widgets have all reported their data, and we must await the
 * first event past that baseline (not a stale earlier collection).
 *
 * The command wait reports WHY the command left the queue, not just that it
 * did, so the client tool narrates an honest result: a command no
 * bridge ever claimed is pruned at a TTL equal to this wait, so "gone" alone
 * cannot tell a finished build from one that never started. On `expired` the
 * collector wait is skipped entirely — it would burn its full timeout and
 * still hand the model an empty digest set, which reads as "built, nothing
 * settled yet" instead of "never began".
 */
async function emitPageGenAndCollect(params: {
  /** Emit the build/update command for this request id (read live deps). */
  emitCommand: (requestId: string) => void;
  emit: ToolEmit | undefined;
  signal: AbortSignal;
  /** Immediate t=0 status before the first stream event lands. */
  preEmitStatus?: string;
  /** Runs when the settle phase starts (before the collector wait). */
  onSettleStart?: () => void;
}): Promise<{
  ok: boolean;
  digests: ChartDataDigest[];
  expectedCount?: number;
  failureReason?: CommandExpiredOutcome;
}> {
  const { emitCommand, emit, signal, preEmitStatus, onSettleStart } = params;
  const requestId = crypto.randomUUID();
  const store = useGlobalAiCommandStore.getState();
  let lastProgressSeq = store.channels.pageGenProgress.seq;
  const baselineCollectionSeq = store.channels.pageCollection.seq;
  let settled = false;
  const unsubscribeProgress = useGlobalAiCommandStore.subscribe((state) => {
    if (settled || signal.aborted) {
      return;
    }
    for (const { seq, event } of channelEventsSince(
      state.channels.pageGenProgress,
      lastProgressSeq
    )) {
      lastProgressSeq = seq;
      emit?.(routePageGenProgress(event));
    }
  });
  try {
    if (preEmitStatus) {
      emit?.({ tier: 'status', text: preEmitStatus });
    }
    emitCommand(requestId);
    const commandOutcome = await waitForCommandComplete(
      requestId,
      COMMAND_COMPLETE_TIMEOUT_MS,
      signal
    );
    if (commandOutcome === 'aborted') {
      // The user stopped the turn. The command is still QUEUED and stays
      // claimable for the rest of its TTL, so a destination bridge that mounts
      // a moment later would run the page-gen they just cancelled. Withdraw
      // it. No-ops if a bridge already claimed it — that run owns its own
      // teardown.
      useGlobalAiCommandStore.getState().cancelIfUnclaimed(requestId);
      return { ok: false, digests: [] };
    }
    if (commandOutcome === 'expired') {
      return { ok: false, digests: [], failureReason: 'expired' };
    }
    if (commandOutcome !== 'completed') {
      // 'timeout' only: a bridge claimed it and is still working, or the wait
      // was shorter than the TTL and it may yet be claimed. Leaving it queued
      // is deliberate — unlike an abort, nobody withdrew the request.
      return { ok: false, digests: [] };
    }
    // Settle phase: stop emitting late build status, then await the collector
    // so we hand the model the page's real widget digests.
    settled = true;
    onSettleStart?.();
    const outcome = await awaitPageCollection(baselineCollectionSeq, signal);
    return {
      ok: true,
      digests: outcome?.digests ?? [],
      expectedCount: outcome?.expectedCount,
    };
  } finally {
    settled = true;
    unsubscribeProgress();
  }
}

/**
 * Await the first `pageCollection` channel event published after
 * `baselineSeq` (i.e. a NEW collection published after we emitted the build
 * command). Returns the collection payload, or `undefined` on timeout or
 * abort (drawer closed).
 */
async function awaitPageCollection(
  baselineSeq: number,
  signal?: AbortSignal
): Promise<GlobalAiChannelPayloads['pageCollection'] | undefined> {
  const hit = await waitForChannelEvent(
    'pageCollection',
    baselineSeq,
    PAGE_COLLECTION_TIMEOUT_MS,
    signal
  );
  return hit?.event;
}
